import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FillInfo } from '@components/forms/invite-guest/fill-info';
import { SelectType } from '@components/forms/invite-guest/select-type';
import { IconDownload } from '@components/icons';
import { SummarySuccessModalContent } from '@components/modals';
import { TicketNotAvailableModal } from '@components/ticket-not-available-modal';
import { STEP_INFO, STEP_SELECT_TYPE, STEP_SUCCESS } from '@constants/process-steps';
import { Addon } from '@domain/addon';
import { Admission } from '@domain/admission';
import { useCheckTicketDownload } from '@hooks/bookings/use-check-ticket-download';
import { AddGuestResponse } from '@hooks/use-add-guest/use-add-guest';
import { createBookingLink } from '@utils/create-booking-link';

import { ProcessInviteGuestStep } from './process-invite-guest.type';

export interface InviteGuestStepsProps {
  step: ProcessInviteGuestStep;
  setStep: (step: ProcessInviteGuestStep) => void;
  offer?: Admission;
  availableAddons: Addon[];
  addGuestData?: AddGuestResponse;
  email?: string;
  quantity: number;
  isSubmitting?: boolean;
  onSubmit: () => void;
  onClose: () => void;
  newSendInvitationProcess: () => void;
}

/*
 * InviteGuestSteps Component
 *
 * Pure component that renders the specific steps for the Invite Guest process.
 * Receives all data and handlers as props from the parent ProcessInviteGuest component.
 */
export const InviteGuestSteps: React.FC<InviteGuestStepsProps> = ({
  step,
  setStep,
  offer,
  availableAddons,
  addGuestData,
  email,
  quantity,
  isSubmitting,
  onSubmit,
  onClose,
  newSendInvitationProcess,
}) => {
  const { t } = useTranslation();
  const { checkAndDownload, modalProps: ticketModalProps } = useCheckTicketDownload();

  return (
    <>
      {step === STEP_SELECT_TYPE && <SelectType onContinue={() => setStep(STEP_INFO)} offer={offer} />}
      {step === STEP_INFO && <FillInfo onContinue={onSubmit} handleClose={onClose} isSubmitting={isSubmitting} />}
      {step === STEP_SUCCESS && offer && addGuestData && (
        <SummarySuccessModalContent
          title={t('invitations:processInviteGuestSuccessTitle')}
          quantity={quantity}
          offer={offer}
          addons={availableAddons}
          mainActionText={t('invitations:buttonAddNewGuest')}
          onClickSecondaryAction={() =>
            checkAndDownload(addGuestData.token, () => createBookingLink(addGuestData.token))
          }
          secondaryActionIcon={IconDownload}
          headerDescription={
            email ? (
              <span className='text-body-s text-secondary'>
                <Trans
                  i18nKey='offers:processEmailSuccessDescription'
                  components={{ b: <b /> }}
                  values={{ email }}></Trans>
              </span>
            ) : undefined
          }
          secondaryActionText={t('offers:downloadPdf')}
          onClickMainAction={newSendInvitationProcess}
          onClickClose={onClose}
          isInvitation={true}
        />
      )}
      <TicketNotAvailableModal {...ticketModalProps} />
    </>
  );
};
